import React, { useContext, useEffect, useState } from 'react'
import { View, StyleSheet, ScrollView, TouchableOpacity, Platform, Linking } from 'react-native'
import { Text, Radio, RadioGroup, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import * as Updates from 'expo-updates'
import HeaderIconButton from '../../components/HeaderIconButton'
import { constantsAtom, organizationAtom, rolesDictAtom, storesDictAtom, suppliersDictAtom, userRoleAtom, usersDictAtom, inactiveUsersAtom, productUnitsDictAtom, walletsDictAtom, userLocaleAtom, themePreferenceAtom } from '../../global/AtomsProvider'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { HintView } from '../../components/HintView'
import { useAiAssistantsAvailable } from '../../global/AiAssistants/useAiAssistantsAvailable'

const THEME_OPTIONS = ['system', 'light', 'dark']

export default function SettingsScreen({ navigation }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const organization = useAtomValue(organizationAtom)
  const aiAssistantsAvailable = useAiAssistantsAvailable(organization?.useSupabaseForData === true, context.user?.organizationId)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const userLocale = useAtomValue(userLocaleAtom)
  const [themePreference, setThemePreference] = useAtom(themePreferenceAtom)
  const [updateIsLoading, setUpdateIsLoading] = useState(false)

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
  }, [])

  const checkForUpdates = async () => {
    if (Platform.OS !== 'web') {
      try {
        setUpdateIsLoading(true)
        const update = await Updates.checkForUpdateAsync()
        if (update.isAvailable) {
          try {
            setUpdateIsLoading(true)
            alert(t('settings.downloadingUpdateHint'))
            await Updates.fetchUpdateAsync()
            await Updates.reloadAsync()
          } catch(error) {
            console.log(error)
          }
        } else {
          alert(t('settings.noUpdates'))
        }
        setUpdateIsLoading(false)
      } catch (e) {
        console.log(e)
      }
    } else {
      alert(t('settings.noUpdateForWebError'))
    }
  }

  const menuItems = [
    {
      title: t('settings.organizationSettings'),
      onPress: () => navigation.navigate('OrgDetail'),
      permission: 'editSettings',
      testID: 'settings-organization',
    },
    {
      title: t('settings.saleSettings'),
      onPress: () => navigation.navigate('SaleSettings'),
      permission: 'newSaleSettings',
      testID: 'settings-sale-settings',
    },
    {
      title: t('settings.users'),
      onPress: () => navigation.navigate('UniversalList', {atom: usersDictAtom, searchField: 'name', detailScreen: 'UserDetail', title: t('settings.users')}),
      permission: 'editUsers',
      testID: 'settings-users',
    },
    {
      // Disabled users are kept out of usersDictAtom, so they're unreachable from the
      // active list. Surface them here so a disabled user can be re-enabled via
      // UserDetailScreen's existing enable action.
      title: t('settings.inactiveUsers') || 'Inactive Users',
      onPress: () => navigation.navigate('UniversalList', {atom: inactiveUsersAtom, searchField: 'name', detailScreen: 'UserDetail', title: t('settings.inactiveUsers') || 'Inactive Users'}),
      permission: 'editUsers',
      testID: 'settings-inactive-users',
    },
    {
      title: t('settings.roles'),
      onPress: () => navigation.navigate('UniversalList', {atom: rolesDictAtom, searchField: 'name', detailScreen: 'RoleDetail', title: t('settings.roles')}),
      permission: 'editRoles',
      testID: 'settings-roles',
    },
    {
      title: t('settings.stores'),
      onPress: () => navigation.navigate('UniversalList', {atom: storesDictAtom, searchField: 'name', detailScreen: 'StoreDetail', title: t('settings.stores')}),
      permission: 'editSettings',
      testID: 'settings-stores',
    },
    {
      title: t('settings.productCategories'),
      onPress: () => navigation.navigate({name: 'ProductCategories'}),
      permission: 'editProductCategories',
      testID: 'settings-product-categories',
    },
    {
      title: t('settings.suppliers'),
      onPress: () => navigation.navigate('UniversalList', {atom: suppliersDictAtom, searchField: 'name', detailScreen: 'SupplierDetail', title: t('settings.suppliers')}),
      permission: 'editSuppliers',
      testID: 'settings-suppliers',
    },
    {
      title: t('settings.modifiers'),
      onPress: () => navigation.navigate('UniversalList', {atom: constantsAtom, initialDataPath: ['productModifiers', 'data'], searchField: 'name', detailScreen: 'ProductModifiersDetail', title: t('settings.modifiers')}),
      permission: 'editProductCategories',
      testID: 'settings-modifiers',
    },
    {
      title: t('settings.discountOptions'),
      onPress: () => navigation.navigate('EditableList', {atom: constantsAtom, initialDataPath: ['discountOptions', 'data'], title: t('settings.discountOptions'), ref: constants?.discountOptions.ref, headerComponent: () => (
        <HintView
          iconName='info-outline'
          style={{marginVertical: 10}}
          text={t('settings.discountInstructions')}
        />
      )}),
      permission: 'editSettings',
      testID: 'settings-discount-options',
    },
    {
      title: t('settings.printSettings'),
      onPress: () => navigation.navigate('PrintSettings'),
      permission: 'editSettings',
      testID: 'settings-print-settings',
    },
    {
      title: t('settings.wallets'),
      onPress: () => navigation.navigate('UniversalList', {atom: walletsDictAtom, searchField: 'text', detailScreen: 'WalletDetail', title: t('settings.wallets')}),
      permission: 'editSettings',
      testID: 'settings-wallets',
    },
    {
      title: t('settings.paymentMethods'),
      onPress: () => navigation.navigate('UniversalList', {atom: constantsAtom, initialDataPath:  ['paymentMethods', 'data'], searchField: 'name', detailScreen: 'PaymentMethodDetail', title: t('settings.paymentMethods')}),
      permission: 'editSettings',
      testID: 'settings-payment-methods',
    },
    {
      title: t('settings.purchaseMethods'),
      onPress: () => navigation.navigate('UniversalList', {atom: constantsAtom, initialDataPath:  ['purchaseMethods', 'data'], searchField: 'name', detailScreen: 'PurchaseMethodDetail', title: t('settings.purchaseMethods')}),
      permission: 'editSettings',
      testID: 'settings-purchase-methods',
    },
    {
      title: t('settings.priceTypes') || 'Price Types',
      onPress: () => navigation.navigate('PriceTypes'),
      permission: 'editSettings',
      testID: 'settings-price-types',
    },
    {
      title: t('settings.productUnits'),
      onPress: () => navigation.navigate('UniversalList', {atom: productUnitsDictAtom, searchField: 'name', detailScreen: 'ProductUnitDetail', title: t('settings.productUnits')}),
      permission: 'editSettings',
      testID: 'settings-product-units',
    },
    {
      title: t('settings.loyaltySettings'),
      onPress: () => navigation.navigate('LoyaltySettings'),
      permission: 'editSettings',
      module: 'clients',
      testID: 'settings-loyalty',
    },
    {
      title: t('settings.marketplaceSettings'),
      onPress: () => navigation.navigate('MarketplaceSettings'),
      permission: 'editSettings',
      module: 'ecommerce',
      testID: 'settings-marketplace',
    },
    {
      title: t('settings.writeOffReasons'),
      onPress: () => navigation.navigate('EditableList', {atom: constantsAtom, initialDataPath: ['writeOffReasons', 'data'], title: t('settings.writeOffReasons'), ref: constants?.writeOffReasons.ref}),
      permission: 'editWriteOffReasons',
      module: 'writeOffs',
      testID: 'settings-writeoff-reasons',
    },
    {
      title: t('settings.returnReasons'),
      onPress: () => navigation.navigate('EditableList', {atom: constantsAtom, initialDataPath: ['returnReasons', 'data'], title: t('settings.returnReasons'), ref: constants?.returnReasons.ref}),
      permission: 'editReturnCategories',
      testID: 'settings-return-reasons',
    },
    {
      title: t('settings.recurringSpendings'),
      onPress: () => navigation.navigate('UniversalList', {atom: constantsAtom, initialDataPath: ['recurringSpendings', 'data'], title: t('settings.recurringSpendings'), searchField: 'name', detailScreen: 'RecurringSpendingDetail'}),
      role: 'admin',
      permission: 'editRecurringSpendings',
      testID: 'settings-recurring-spendings',
    },
    {
      title: t('settings.adminSpendingCategories'),
      onPress: () => navigation.navigate('EditableList', {atom: constantsAtom, initialDataPath: ['adminSpendingCategories', 'data'], title: t('settings.adminSpendingCategories'), ref: constants?.adminSpendingCategories.ref}),
      role: 'admin',
      permission: 'editSpendingCategories',
      module: 'spendings',
      testID: 'settings-admin-spending-categories',
    },
    {
      title: t('settings.spendingCategories'),
      onPress: () => navigation.navigate('EditableList', {atom: constantsAtom, initialDataPath: ['spendingCategories', 'data'], title: t('settings.spendingCategories'), ref: constants?.spendingCategories.ref}),
      role: 'admin',
      permission: 'editSpendingCategories',
      module: 'spendings',
      testID: 'settings-spending-categories',
    },
    {
      title: t('exportImport.exportImport'),
      onPress: () => navigation.navigate('ExportImport'),
      permission: 'exportImport',
      testID: 'settings-export-import',
    },
    {
      title: t('salesImport.title'),
      onPress: () => navigation.navigate('SalesImport'),
      permission: 'exportImport',
      module: 'sales',
      testID: 'settings-sales-import',
    },
    {
      title: t('profile.profile'),
      onPress: () => navigation.navigate('Profile'),
      testID: 'settings-profile',
    },
    {
      title: t('settings.billing'),
      onPress: () => navigation.navigate('Billing'),
      permission: 'billing',
      testID: 'settings-billing',
    },
    {
      title: t('helpCenter.helpCenter'),
      onPress: () => Linking.openURL('https://atlas-app.notion.site/745b5f835e9144ecb022d729e6b2e23b?v=d8f2555a75e044e5a18d6bbc7e0cb4b8')
    },
    {
      title: t('helpCenter.whatIsNew'),
      onPress: () => Linking.openURL('https://atlas-app.notion.site/1dc64b32d20a4bc1bb226d9def6295ca?v=3cc60583386f4bae8a0bedf09a71b9ed')
    },
    {
      title: t('helpCenter.contactSupport'),
      onPress: () => Linking.openURL(
          'https://atlas-app.org/' + userLocale + '/contact-us?name=' + context.user.name + '&phone=' + context.user.phone + '&email=' + context.user.email
          + '&organizationId=' + context.user.organizationId + '&organizationName=' + organization?.name
          + '&userId=' + context.user.uid + '&category=' + t('helpCenter.contactSupport')
        ),
    },
    {
      // ATL-1440: organization tokens for AI agents. Admins only — the backend refuses anyone else,
      // so this is not gated by a role permission a custom role could be granted.
      title: t('settings.apiTokens'),
      onPress: () => navigation.navigate('ApiTokens'),
      adminOnly: true,
      testID: 'settings-api-tokens',
    },
    {
      // ATL-1694: how to connect Claude / ChatGPT over MCP. Shown to everyone in an organization the
      // server says can connect one now (the guide itself says only an admin can approve), and to
      // nobody while the server is dark or the organization is not admitted.
      title: t('settings.aiAssistants'),
      onPress: () => navigation.navigate('AiAssistants'),
      visible: aiAssistantsAvailable,
      testID: 'settings-ai-assistants',
    },
    {
      // ATL-1445: sales and acceptances an AI agent proposed through Atlas API v1, for the merchant
      // to open in New Sale / the acceptance form or dismiss. API tokens exist only for organizations
      // reading from Supabase, so nobody else can have a draft. The list itself is filtered by the
      // backend to the kinds this person's role may act on (NewSale / Supplies).
      title: t('settings.agentDrafts'),
      onPress: () => navigation.navigate('AgentDrafts'),
      permissionsAny: ['NewSale', 'Supplies'],
      visible: organization?.useSupabaseForData === true,
      testID: 'settings-agent-drafts',
    },
    {
      title: t('settings.legal'),
      onPress: () => navigation.navigate('Legal'),
      permission: 'billing',
    },
    {
      title: t('settings.checkForUpdates'),
      onPress: () => checkForUpdates(),
    },
    // {
    //   title: t('settings.resetData'),
    //   onPress: () => navigation.navigate('ResetData'),
    //   permission: 'resetData',
    // },
  ]

  return (
    <ScrollView style={styles.container}>
        {
          menuItems.map((item, index) => (
            ((userRole?.permissions?.[item.permission] || context.user?.role === 'admin' || !item.permission) && 
            (item.organizationIds == null || item.organizationIds.includes(context.user?.organizationId))) && 
            (item.module == null || organization?.moduleEnabled?.[item.module]) &&
            (item.platform == null || item.platform === Platform.OS) &&
            (!item.adminOnly || context.user?.role === 'admin') &&
            (item.visible == null || item.visible) &&
            (item.permissionsAny == null || context.user?.role === 'admin' || item.permissionsAny.some((p) => userRole?.permissions?.[p])) &&
            (
              <TouchableOpacity
                style={[styles.item, {backgroundColor: theme['background-basic-color-1']}]}
                onPress={item.onPress}
                key={index.toString()}
                testID={item.testID}
                {...(Platform.OS === 'web' && item.testID ? { dataSet: { testid: item.testID } } : {})}
              >
                <Text
                  style={styles.itemTitle}
                  category='s2'>
                  {item.title}
                </Text>
              </TouchableOpacity>
            )
          ))
        }
        <View style={[styles.themeSection, {backgroundColor: theme['background-basic-color-1']}]}>
          <Text category='s1' style={styles.themeSectionTitle}>
            {t('settings.appearance') || 'Appearance'}
          </Text>
          <RadioGroup
            selectedIndex={THEME_OPTIONS.indexOf(themePreference)}
            onChange={index => setThemePreference(THEME_OPTIONS[index])}
          >
            <Radio>{t('settings.systemTheme') || 'System'}</Radio>
            <Radio>{t('settings.lightTheme') || 'Light'}</Radio>
            <Radio>{t('settings.darkTheme') || 'Dark'}</Radio>
          </RadioGroup>
        </View>
        <View style={{height:50}}></View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  item: {
    borderRadius: 10,
    padding: 10,
    marginVertical: 5
  },
  themeSection: {
    marginTop: 15,
    borderRadius: 10,
    padding: 15,
  },
  themeSectionTitle: {
    marginBottom: 10,
  },
});
